import React, { useState, useMemo } from 'react';
import '../../assets/css/case-property.css';
import cornerIcon from '../../assets/images/case-image-corner.svg';
// @ts-ignore
import { useProperty, useContentToken, EditableToken, useCollectionBinding, RichTextContent } from '@vibesites/runtime/react';
import type { ComponentContentSchema, CollectionBindingDefinition } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getPropertyBasePath } from './propertyUtils';

/**
 * Floor plans preview section: dark text column on left + single-card carousel on right.
 * Matches the Stratman wp-block-stratman-content-columns bg-dark + plans-slider pattern.
 */
export function PropertyFloorPlansPreview() {
  const property = useProperty();
  const rawFloorPlans = useCollectionBinding<any>('propertyfloorplans.plans', { collection: 'floorplans' });
  // Sort by bedrooms ascending, then bathrooms ascending
  const floorPlans = useMemo(() => {
    return [...rawFloorPlans].sort((a: any, b: any) => {
      const aBeds = Number(a.floorplan_beds_min || a.beds || 0);
      const bBeds = Number(b.floorplan_beds_min || b.beds || 0);
      if (aBeds !== bBeds) return aBeds - bBeds;
      const aBaths = Number(a.floorplan_baths_min || a.baths || 0);
      const bBaths = Number(b.floorplan_baths_min || b.baths || 0);
      return aBaths - bBaths;
    });
  }, [rawFloorPlans]);
  const [activeIndex, setActiveIndex] = useState(0);

  const basePath = getPropertyBasePath(property);
  const name = property.property_name || '';
  const city = property.property_address_city || '';
  const defaultTitle = city
    ? `1 Bedroom, 2 Bedroom & 3 Bedroom Luxury Apartment Floor Plans for Rent in ${city}`
    : `${name} Floor Plans`;
  const title = useContentToken('propertyfloorplans.title') || defaultTitle;
  const ctaText = useContentToken('propertyfloorplans.cta_text');

  const total = floorPlans.length;
  const goPrev = () => setActiveIndex(i => (i - 1 + total) % total);
  const goNext = () => setActiveIndex(i => (i + 1) % total);

  return (
    <section className="prop-floorplans-section">
      <div className="prop-floorplans-grid">
        {/* Dark text column */}
        <div className="prop-floorplans-text">
          <EditableToken path="propertyfloorplans.title" type="text" label="Section Title" as="h2" className="prop-section-title" defaultValue={contentSchema.tokens['propertyfloorplans.title'].defaultValue}>
            {title}
          </EditableToken>
          <div className="prop-floorplans-separator" />
          <RichTextContent
            path="propertyfloorplans.blurb"
            label="Description"
            defaultField={contentSchema.tokens['propertyfloorplans.blurb'].defaultField}
            defaultValue={contentSchema.tokens['propertyfloorplans.blurb'].defaultValue}
            as="div"
          />
          <EditableToken path="propertyfloorplans.cta_text" type="text" label="CTA Button Text" as="span" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['propertyfloorplans.cta_text'].defaultValue}>
            <a className="prop-btn prop-floorplans-cta" href={`${basePath}/floor-plans/`}>{ctaText}</a>
          </EditableToken>
        </div>

        {/* Single-card carousel column - slide carousel with image + specs as one unit */}
        <div className="prop-floorplans-carousel">
          {total > 0 ? (
            <>
              <div className="prop-floorplans-card-wrap" style={{ position: 'relative' }}>
                <img src={cornerIcon} alt="" className="prop-corner-icon prop-corner-icon--tr" aria-hidden="true" />
                <div className="prop-floorplans-slide-viewport">
                  <div
                    className="prop-floorplans-slide-track"
                    style={{ width: `${total * 100}%`, transform: `translateX(-${(activeIndex / total) * 100}%)` }}
                  >
                    {floorPlans.map((plan: any, idx: number) => {
                      const planImage = plan.floorplan_image_url || plan.image || '';
                      const planBeds = plan.floorplan_beds_min || plan.beds || '';
                      const planBaths = plan.floorplan_baths_min || plan.baths || '';
                      const planSqft = plan.floorplan_sqft_min || plan.sqft || '';
                      return (
                        <div key={idx} className="prop-floorplans-slide-card" style={{ flex: `0 0 ${100 / total}%` }}>
                          <div className="prop-floorplans-card">
                            {planImage && (
                              <VibesitesImage url={planImage} alt={plan.floorplan_name || plan.name || `Floor plan ${idx + 1}`} sizes="(min-width: 992px) 50vw, 100vw" />
                            )}
                            <p className="prop-floorplans-disclaimer">
                              For illustrative purposes only. Materials, measurements and details might differ from the actual property.
                            </p>
                            <div className="prop-floorplans-specs">
                              <div className="prop-plan-card-specs">
                              {planBeds && (
                                <span className="prop-plan-spec">
                                  <svg className="prop-plan-spec-icon" viewBox="0 0 22.5 20" width="23" height="20" fill="currentColor"><path d="M12.5,0,2.5,2.5v15H0V20H12.5ZM10,10c0,.691-.418,1.25-.937,1.25s-.937-.559-.937-1.25.418-1.25.938-1.25S10,9.309,10,10Zm3.75-5H17.5V20h5V17.5H20V2.5H13.75Z"/></svg>
                                  <strong>{planBeds} {Number(planBeds) === 1 ? 'bed' : 'beds'}</strong>
                                </span>
                              )}
                              {planBaths && (
                                <span className="prop-plan-spec">
                                  <svg className="prop-plan-spec-icon" viewBox="0 0 20 20" width="23" height="20" fill="currentColor"><path d="M2.5,0H1.25V10H0v2.5H20V10H3.75V2.5h.73l.734.734A3.15,3.15,0,0,0,5,4.375a3.1,3.1,0,0,0,.73,2.008l-.492.492L6.563,8.2l.664-.664,4.063-4.059.664-.664L10.625,1.488l-.492.492a3.1,3.1,0,0,0-2.008-.73,3.15,3.15,0,0,0-1.141.215l-1.1-1.1L5.52,0H2.5ZM1.25,13.75v.625a3.753,3.753,0,0,0,1.25,2.8V20H5V18.125H15V20h2.5V17.172a3.747,3.747,0,0,0,1.25-2.8V13.75Z"/></svg>
                                  <strong>{planBaths} Bath</strong>
                                </span>
                              )}
                              {planSqft && (
                                <span className="prop-plan-spec">
                                  <svg className="prop-plan-spec-icon" viewBox="0 0 512 512" width="23" height="20" fill="currentColor"><path d="M.2 468.9C2.7 493.1 23.1 512 48 512l96 0 320 0c26.5 0 48-21.5 48-48l0-96c0-26.5-21.5-48-48-48l-48 0 0 80c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-80-64 0 0 80c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-80-64 0 0 80c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-80-80 0c-8.8 0-16-7.2-16-16s7.2-16 16-16l80 0 0-64-80 0c-8.8 0-16-7.2-16-16s7.2-16 16-16l80 0 0-64-80 0c-8.8 0-16-7.2-16-16s7.2-16 16-16l80 0 0-48c0-26.5-21.5-48-48-48L48 0C21.5 0 0 21.5 0 48L0 368l0 96c0 1.7 .1 3.3 .2 4.9z"/></svg>
                                  <strong>{planSqft} Sq. Ft.</strong>
                                </span>
                              )}
                              </div>
                              <a className="prop-plan-card-link" href={`${basePath}/floor-plans`} aria-label="View floor plan details">
                                <svg className="prop-plan-arrow" viewBox="0 0 11.913 11" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M75.57,70.072a.918.918,0,0,0,0-1.3l-4.582-4.582a.917.917,0,0,0-1.3,1.3l3.934,3.934-3.932,3.934a.917.917,0,0,0,1.3,1.3l4.582-4.582ZM65.491,74.654l4.582-4.582a.918.918,0,0,0,0-1.3l-4.582-4.582a.917.917,0,0,0-1.3,1.3l3.934,3.934L64.2,73.359a.917.917,0,0,0,1.3,1.3Z" transform="translate(-63.925 -63.925)"/></svg>
                              </a>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {total > 1 && (
                  <>
                    <button className="prop-carousel-arrow prop-carousel-arrow-prev" onClick={goPrev} aria-label="Previous slide" type="button">
                      <svg viewBox="0 0 16 16" width="20" height="20" fill="currentColor"><path d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/></svg>
                    </button>
                    <button className="prop-carousel-arrow prop-carousel-arrow-next" onClick={goNext} aria-label="Next slide" type="button">
                      <svg viewBox="0 0 16 16" width="20" height="20" fill="currentColor"><path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/></svg>
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="prop-floorplans-empty">
              <p>Floor plan details coming soon.</p>
              <a className="prop-btn" href={`${basePath}/floor-plans/`}>View Floor Plans</a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyFloorPlans',
  displayName: 'Property Floor Plans Preview',
  tokens: {
    'propertyfloorplans.title': {
      label: 'Section Title',
      type: 'text',
      defaultValue: 'Luxury Apartment Floor Plans for Rent',
      description: 'Defaults to "1 Bedroom, 2 Bedroom & 3 Bedroom Luxury Apartment Floor Plans for Rent in {city}"',
    },
    'propertyfloorplans.blurb': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_home_floorplans_blurb',
      defaultValue: 'Explore our selection of floor plans designed to fit your lifestyle.',
    },
    'propertyfloorplans.cta_text': {
      label: 'CTA Button Text',
      type: 'text',
      defaultValue: 'View Floor Plans',
    },
  },
  collectionBindings: {
    'propertyfloorplans.plans': {
      label: 'Floor Plans',
      collection: 'floorplans',
      description: 'Floor plan cards displayed in the single-card carousel',
    } as CollectionBindingDefinition,
  },
};

(PropertyFloorPlansPreview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyFloorPlansPreview;
